import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CreditCard, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function PayBooking() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bookings/public/${token}`).then((r) => setB(r.data)).catch(() => setError("We couldn't find that booking."));
  }, [token]);

  const pay = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bookings/public/${token}/pay`, { origin_url: window.location.origin }, { timeout: 60000 });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open the payment page — please call 310-422-0669");
      setBusy(false);
    }
  };

  if (error) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-300" data-testid="pay-notfound">{error} <Link to="/book" className="dg-link">Book a session</Link></div>;
  if (!b) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-400">Loading your booking…</div>;
  const paid = b.payment_status === "paid";
  const due = Number(b.amount_due || 0);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16" data-testid="pay-page">
      <div className="dg-card rounded-2xl p-8 text-center">
        {paid ? <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" /> : <CreditCard className="w-12 h-12 text-[#FFD600] mx-auto" />}
        <div className="dg-eyebrow mt-4">{paid ? "Paid — thank you" : "Session payment"}</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold font-display" data-testid="pay-title">
          {paid ? "This session is all paid up." : due > 0 ? `Pay $${due.toFixed(2)} for your session` : "Nothing to pay for this session"}
        </h1>
        <p className="text-slate-300 mt-3"><strong className="text-white">{b.service_name}</strong><br />{b.when}</p>
        <p className="text-slate-400 mt-1 text-sm">Booking <span className="font-mono text-cyan-300">{b.booking_id}</span>{b.member_tier_name && <span className="text-[#FFD600]"> · {b.member_tier_name} member price</span>}</p>
        {!paid && due > 0 && (
          <>
            <button type="button" onClick={pay} disabled={busy} className="dg-btn-primary rounded-full px-8 py-3 text-base inline-flex items-center gap-2 mt-8 disabled:opacity-50" data-testid="pay-now-btn">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} {busy ? "Opening secure checkout…" : `Pay $${due.toFixed(2)} by card`}
            </button>
            <p className="text-xs text-slate-500 mt-4 flex items-center justify-center gap-1" data-testid="pay-secure-note"><ShieldCheck className="w-3.5 h-3.5" /> Secure checkout by Stripe · card details never touch our servers · receipt by email</p>
          </>
        )}
        {(paid || due <= 0) && <p className="text-sm text-slate-400 mt-6">{b.status === "cancelled" ? "This session was cancelled." : "Questions? Call 310-422-0669."}</p>}
        <Link to={`/book/manage/${token}`} className="dg-link text-sm inline-block mt-6" data-testid="pay-back-link">Back to your booking</Link>
      </div>
    </div>
  );
}
